import React, { useEffect, useState, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import { Section, Button, Empty, Input } from "@/components/ui/kit";
import { fmtCurrency, fmtDate } from "@/utils/format"; 
import { Plus, Trash2, Search, ChevronDown } from "lucide-react";
import { toast } from "sonner";

export default function UtilizationsList() {
  const [rows, setRows] = useState([]);
  const [purposes, setPurposes] = useState([]);
  
  // Filter & Pagination States
  const [sectorId, setSectorId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [activityId, setActivityId] = useState("");
  
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  // Fetch Purposes for the Cascading Dropdowns
  useEffect(() => {
    api.get("/donation-purposes").then(r => setPurposes(r.data.data));
  }, []);

  // Compute Cascading Lists
  const sectors = useMemo(() => purposes.filter(p => p.type === "SECTOR"), [purposes]);
  const projects = useMemo(() => purposes.filter(p => p.type === "PROJECT" && (!sectorId || String(p.parentId) === String(sectorId))), [purposes, sectorId]);
  const activities = useMemo(() => purposes.filter(p => p.type === "ACTIVITY" && (!projectId || String(p.parentId) === String(projectId))), [purposes, projectId]);

  // Determine the most specific purpose ID to send to the backend
  const activePurposeId = activityId || projectId || sectorId || "";

  // Fetch Utilizations from the API
  const load = () => {
    setLoading(true);
    api.get("/utilizations", { 
      params: { 
        page, 
        size: 20, 
        purposeId: activePurposeId || undefined 
      } 
    })
    .then(r => {
      setRows(r.data.data.content);
      setTotalPages(r.data.data.totalPages || 1);
    })
    .catch(() => toast.error("Failed to load utilizations"))
    .finally(() => setLoading(false));
  };

  // Reload whenever page or purpose filters change
  useEffect(() => { load(); }, [page, activePurposeId]);

  const handleDelete = async (id) => {
    if (window.confirm(`Are you sure you want to delete utilization #${id}?`)) {
      try {
        await api.delete(`/utilizations/${id}`);
        toast.success("Utilization deleted successfully");
        load();
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to delete utilization");
      }
    }
  };

  // Local Search Filter (makes finding a specific row instantly snappy without hitting DB)
  const displayedRows = useMemo(() => {
    if (!searchTerm) return rows;
    const term = searchTerm.toLowerCase();
    return rows.filter(r => 
      String(r.utilizationId).includes(term) ||
      r.purposeName?.toLowerCase().includes(term) ||
      r.description?.toLowerCase().includes(term)
    );
  }, [rows, searchTerm]);

  return (
    <div className="space-y-6">
      <Section title="Fund Utilization" kicker="Where the money goes"
        right={<Link to="/admin/utilizations/new"><Button data-testid="add-utilization-btn"><Plus className="w-4 h-4 mr-2"/> Record utilization</Button></Link>}
      />
      
      <div className="rounded-xl border border-emerald-900/10 bg-white p-4">
        
        {/* 4-Column Filter Grid with Searchable Dropdowns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <div className="relative">
            <Input 
              placeholder="Search ID, purpose, notes..." 
              value={searchTerm} 
              onChange={e => setSearchTerm(e.target.value)} 
              className="pl-9"
            />
            <Search className="w-4 h-4 text-emerald-900/40 absolute left-3 top-3 pointer-events-none" />
          </div>
          
          <SearchableSelect 
            placeholder="Search Sector..." 
            options={sectors} 
            value={sectorId} 
            onChange={val => { setSectorId(val); setProjectId(""); setActivityId(""); setPage(0); }} 
          />

          <SearchableSelect 
            placeholder="Search Project..." 
            options={projects} 
            value={projectId} 
            disabled={!sectorId && projects.length === 0}
            onChange={val => { setProjectId(val); setActivityId(""); setPage(0); }} 
          />

          <SearchableSelect 
            placeholder="Search Activity..." 
            options={activities} 
            value={activityId} 
            disabled={!projectId && activities.length === 0}
            onChange={val => { setActivityId(val); setPage(0); }} 
          />
        </div>

        {/* Data Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-widest text-emerald-800/70 border-b border-emerald-900/10">
                <th className="py-2 pr-4">ID</th>
                <th className="py-2 pr-4">Purpose</th>
                <th className="py-2 pr-4">Donations (Base INR)</th>
                <th className="py-2 pr-4">Date</th>
                <th className="py-2 pr-4">Event Amount</th>
                <th className="py-2 pr-4">Beneficiaries</th>
                <th className="py-2 pr-4 max-w-[200px]">Notes</th>
                <th className="py-2 pr-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayedRows.map(r => {
                const currencyCode = r.amountType || r.currencyCode || "INR";
                
                return (
                  <tr key={r.utilizationId} data-testid={`utilization-row-${r.utilizationId}`} className="border-b border-emerald-900/5 hover:bg-emerald-50/40">
                    <td className="py-3 pr-4 font-mono text-emerald-900">#{r.utilizationId}</td>
                    <td className="py-3 pr-4 font-semibold text-emerald-950">{r.purposeName}</td>
                    <td className="py-3 pr-4 font-mono text-xs text-emerald-800 hover:underline">
                      {r.sourceCount > 1 
                        ? <span className="text-muted-foreground">Multiple Sources</span> 
                        : r.donationId 
                          ? <Link to={`/admin/donations/${r.donationId}`}>#{r.donationId}</Link> 
                          : <span className="text-muted-foreground">General Pool</span>}
                    </td>
                    <td className="py-3 pr-4 text-muted-foreground">{fmtDate(r.date)}</td>
                    
                    <td className="py-3 pr-4 font-mono font-semibold text-orange-800">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-50 border border-orange-900/10 text-orange-800 font-sans font-medium">
                          {currencyCode}
                        </span>
                        <span>{fmtCurrency(r.amount, currencyCode)}</span>
                      </div>
                    </td>
                    
                    <td className="py-3 pr-4 font-mono">{r.beneficiaryCount}</td>
                    <td className="py-3 pr-4 text-muted-foreground line-clamp-1 max-w-[200px]" title={r.description}>{r.description || "—"}</td>
                    <td className="py-3 pr-4 text-right">
                      <div className="flex justify-end gap-3 items-center">
                        <Link to={`/admin/utilizations/${r.utilizationId}`} className="text-emerald-800 font-semibold text-xs hover:underline">
                          View
                        </Link>
                        <button onClick={() => handleDelete(r.utilizationId)} className="text-red-600 hover:text-red-800 transition-colors" title="Delete">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!loading && !displayedRows.length && (
                <tr><td colSpan={8}><Empty title="No match found" note="Try clearing your filters or searching a different term" testid="utilizations-empty" /></td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        <div className="mt-4 flex items-center justify-between text-xs">
          <div className="text-muted-foreground">Page {page + 1} of {Math.max(1, totalPages)}</div>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" disabled={page === 0 || loading} onClick={() => setPage(p => p - 1)}>Prev</Button>
            <Button variant="secondary" size="sm" disabled={page + 1 >= totalPages || loading} onClick={() => setPage(p => p + 1)}>Next</Button>
          </div>
        </div>

      </div>
    </div>
  );
}

// --- Custom Searchable Select Component ---
function SearchableSelect({ value, onChange, options, placeholder, disabled }) {
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Sync internal search state with the selected value prop
  useEffect(() => {
    if (value) {
      const selectedOpt = options.find(o => String(o.id) === String(value));
      if (selectedOpt) setSearch(selectedOpt.name);
    } else {
      setSearch("");
    }
  }, [value, options]);

  // Handle clicking outside the dropdown to close it
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        // If they typed something but didn't select, revert text back to selected item
        const selectedOpt = options.find(o => String(o.id) === String(value));
        setSearch(selectedOpt ? selectedOpt.name : "");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [value, options]);

  const filtered = options.filter(o => o.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="relative" ref={wrapperRef}>
      <Input
        placeholder={placeholder}
        value={search}
        disabled={disabled}
        onChange={e => {
          setSearch(e.target.value);
          setIsOpen(true);
          if (value) onChange(""); // clear actual selection if user starts typing a new search
        }}
        onClick={() => { if (!disabled) setIsOpen(true); }}
      />
      <ChevronDown className={`absolute right-3 top-3 w-4 h-4 pointer-events-none transition-transform ${isOpen ? "rotate-180" : ""} ${disabled ? "text-emerald-900/20" : "text-emerald-900/40"}`} />

      {isOpen && !disabled && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1 max-h-60 overflow-y-auto bg-white border border-emerald-900/10 rounded-lg shadow-xl">
          {filtered.length === 0 ? (
            <div className="px-4 py-3 text-sm text-center text-muted-foreground">No matches found</div>
          ) : (
            <>
              {/* Provide a clear option if something is already selected */}
              {value && (
                <button
                  type="button"
                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 border-b border-emerald-50 transition-colors"
                  onClick={() => { onChange(""); setSearch(""); setIsOpen(false); }}
                >
                  Clear Selection
                </button>
              )}
              {filtered.map(opt => (
                <button
                  key={opt.id}
                  type="button"
                  className={`w-full text-left px-4 py-2.5 text-sm hover:bg-emerald-50 transition-colors ${String(value) === String(opt.id) ? "bg-emerald-50/50 font-semibold text-emerald-900" : "text-emerald-950"}`}
                  onClick={() => {
                    onChange(opt.id);
                    setSearch(opt.name);
                    setIsOpen(false);
                  }}
                >
                  {opt.name}
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}